import { Link, useParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { getWorkById, works } from "@/mocks/works";
import { getCharacterById } from "@/mocks/characters";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import WorkCard from "@/components/feature/WorkCard";
import ShareButtons from "@/components/feature/ShareButtons";

export default function WorkDetail() {
  const { id } = useParams<{ id: string }>();
  const work = id ? getWorkById(id) : undefined;

  useSeo({
    title: work ? `《${work.title}》介绍 - 希岛爱理影视作品` : "作品未找到 - 希岛爱理影视作品",
    description: work
      ? `${work.title}（${work.year}）${work.type}，导演${work.director}，主演${work.cast.join("、")}。查看剧情简介、角色信息与相关推荐。`
      : "未找到对应的影视作品。",
    keywords: work ? `${work.title},${work.type},${work.director},希岛爱理影视作品` : "希岛爱理影视作品",
  });

  if (!work) {
    return (
      <div className="w-full px-4 md:px-8 py-20 text-center">
        <i className="ri-error-warning-line text-4xl text-foreground-300" />
        <h1 className="font-heading text-xl font-bold text-foreground-950 mt-4">未找到该作品</h1>
        <p className="text-sm text-foreground-500 mt-2">作品可能已被移除或地址有误。</p>
        <Link
          to="/works"
          className="mt-6 inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line" />
          返回作品列表
        </Link>
      </div>
    );
  }

  const roles = work.roleIds
    .map((rid) => getCharacterById(rid))
    .filter((c): c is NonNullable<typeof c> => Boolean(c));

  const related = works
    .filter((w) => w.id !== work.id && (w.type === work.type || Math.abs(w.year - work.year) <= 1))
    .slice(0, 4);

  const meta = [
    { label: "类型", value: work.type, icon: "ri-price-tag-3-line" },
    { label: "上映时间", value: work.releaseDate, icon: "ri-calendar-line" },
    { label: "时长", value: work.runtime, icon: "ri-time-line" },
    { label: "导演", value: work.director, icon: "ri-clapperboard-line" },
    { label: "主演", value: work.cast.join("、"), icon: "ri-user-star-line" },
  ];

  return (
    <div className="w-full pb-16">
      <Breadcrumbs
        items={[{ label: "首页", to: "/" }, { label: "影视作品", to: "/works" }, { label: work.title }]}
      />

      <div className="w-full px-4 md:px-8 pt-6">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-10">
          <div className="w-full lg:w-[340px] shrink-0">
            <div className="w-full aspect-[2/3] rounded-lg overflow-hidden bg-background-200 border border-background-200">
              <img
                src={work.cover}
                alt={`${work.title} 海报`}
                title={`${work.title} - 希岛爱理影视作品`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-2.5 py-1 rounded-md bg-primary-100 text-primary-800 text-xs font-medium">
                {work.type}
              </span>
              <span className="px-2.5 py-1 rounded-md bg-accent-100 text-accent-800 text-xs font-medium">
                {work.year}
              </span>
            </div>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mt-3">
              《{work.title}》
            </h1>

            <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
              {meta.map((m) => (
                <div key={m.label} className="flex items-start gap-2">
                  <i className={`${m.icon} text-accent-600 mt-0.5`} />
                  <div>
                    <dt className="text-xs text-foreground-500">{m.label}</dt>
                    <dd className="text-sm text-foreground-900">{m.value}</dd>
                  </div>
                </div>
              ))}
            </dl>

            <div className="mt-8 pt-6 border-t border-background-200">
              <h2 className="font-heading text-lg font-bold text-foreground-950 mb-3">作品简介</h2>
              <p className="text-sm md:text-base text-foreground-700 leading-loose">{work.synopsis}</p>
            </div>

            <div className="mt-8">
              <ShareButtons title={`《${work.title}》- 希岛爱理影视作品`} />
            </div>
          </div>
        </div>
      </div>

      {roles.length > 0 && (
        <section className="w-full px-4 md:px-8 pt-14">
          <h2 className="font-heading text-xl font-bold text-foreground-950 mb-6">
            角色与扮演者
            <span className="ml-2 text-sm font-normal text-foreground-500">点击角色查看解析</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {roles.map((c) => (
              <Link
                key={c.id}
                to={`/characters/${c.id}`}
                className="group flex gap-4 p-4 rounded-lg bg-background-50 border border-background-200 hover:border-accent-300 transition-colors cursor-pointer"
              >
                <div className="w-20 h-24 shrink-0 rounded-md overflow-hidden bg-background-200">
                  <img
                    src={c.image}
                    alt={`${c.name} 角色形象`}
                    title={`${c.name} - 希岛爱理角色解析`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading font-semibold text-foreground-950 group-hover:text-accent-700 transition-colors">
                    {c.name}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-0.5">扮演者 · 希岛爱理</p>
                  <p className="text-sm text-foreground-600 mt-2 line-clamp-2 leading-relaxed">
                    {c.description}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {related.length > 0 && (
        <section className="w-full px-4 md:px-8 pt-14">
          <h2 className="font-heading text-xl font-bold text-foreground-950 mb-2">相关推荐</h2>
          <p className="text-sm text-foreground-500 mb-6">
            根据作品类型与上映时间相近度推荐，供延伸浏览参考。
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
            {related.map((w) => (
              <WorkCard key={w.id} work={w} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}